import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useStudents } from '../../hooks/useStudents';
import StatsOverview from './StatsOverview';
import SearchFilter from './SearchFilter';
import ExportButtons from './ExportButtons';
import CompletionBanner from './CompletionBanner';
import StudentList from './StudentList';
import Loading from '../common/Loading';
import ErrorMessage from '../common/ErrorMessage';

const Dashboard = () => {
  const {
    students,
    statistics,
    clusters,
    gradeLevels,
    campuses,
    loading,
    error,
    pagination,
    completionMeta,
    filters,
    updateFilters,
    setView,
    nextPage,
    prevPage,
    refetch,
  } = useStudents();

  const [slowLoading, setSlowLoading] = useState(false);

  useEffect(() => {
    if (!loading) {
      setSlowLoading(false);
      return;
    }
    const timer = setTimeout(() => setSlowLoading(true), 8000);
    return () => clearTimeout(timer);
  }, [loading]);

  if (loading && students.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Loading message={slowLoading ? 'Server is warming up, please wait...' : 'Loading students...'} />
        {slowLoading && (
          <p className="text-center text-sm text-gray-500 mt-2">
            This can take up to a minute after periods of inactivity.
          </p>
        )}
      </div>
    );
  }

  if (error && students.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <ErrorMessage message={error} onRetry={refetch} />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
        <h1 className="text-3xl font-bold text-gray-900">
          {filters.view === 'all' ? 'All Students' : 'Student Survey Results'}
        </h1>

        <div className="inline-flex rounded-lg border border-gray-300 bg-white p-1">
          <button
            onClick={() => setView('completed')}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
              filters.view !== 'all' ? 'bg-primary-600 text-white' : 'text-gray-700 hover:bg-gray-50'
            }`}
          >
            Completed Surveys
          </button>
          <button
            onClick={() => setView('all')}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
              filters.view === 'all' ? 'bg-primary-600 text-white' : 'text-gray-700 hover:bg-gray-50'
            }`}
          >
            All Students
          </button>
        </div>
      </div>

      <StatsOverview statistics={statistics} />

      {filters.view === 'all' && !filters.completion && (
        <CompletionBanner
          meta={completionMeta}
          onShowNotCompleted={() => updateFilters({ completion: 'not_completed' })}
        />
      )}

      <SearchFilter
        filters={filters}
        clusters={clusters}
        gradeLevels={gradeLevels}
        campuses={campuses}
        onFilterChange={updateFilters}
      />

      {filters.view !== 'all' && (
        <ExportButtons
          filters={filters}
          totalStudents={pagination?.totalItems}
        />
      )}

      <StudentList
        students={students}
        pagination={pagination}
        onPrevPage={prevPage}
        onNextPage={nextPage}
      />
    </div>
  );
};

export default Dashboard;
